// R58-L-10 — Tile selection facade over the existing `selectionStore`.
//
// The multi-select primitives (`selectOne`, `toggleSelection`,
// `selectRange`) already live on `useSelectionStore` (added in Story 013,
// kept current through R58-L-13's `activeKind` extension). This hook
// is a thin facade that:
//
//   1. Names the surface (`useTileSelection`) so call-sites in
//      `TilesWidget` + `TilePromoteButton` read by intent.
//   2. Returns a `handleClick(findingId, event)` adapter that mirrors
//      the standard file-manager UX:
//        - plain click          → selectOne(id)
//        - Ctrl/Cmd click       → toggleSelection(id)  (toggles off too,
//                                                       fixes R58-37 bug)
//        - Shift click          → selectRange(id, visibleIds)
//   3. Exposes the live `selectedFindingIds` + `activeFindingId` for the
//      visual selection indicator (white frame) and the Promote button's
//      disabled state.
//
// Story file `_STORIES/lyse/R58-L-10-promote-from-tiles-and-multi-select.md`.

import { useCallback } from "react";
import {
  useSelectionStore,
  type SelectionStoreState,
} from "../state/selectionStore.js";

export interface TileSelectionApi {
  /** Live ids of all selected tiles. */
  selectedIds: Set<string>;
  /** Currently active id — drives the Viewer overlay + List row scroll. */
  activeId: string | null;
  /**
   * Pointer-event handler. The caller passes the visible-order list of
   * finding ids so Shift+click can compute the inclusive range against
   * the rendered grid (which respects the analysis-filter cascade —
   * Blade / Side / Severity / Status). Falls back to `selectOne` when
   * the anchor is no longer in `visibleIds` (e.g. the user filtered the
   * anchor out between clicks).
   */
  handleClick: (
    findingId: string,
    event: React.MouseEvent<HTMLElement>,
    visibleIds: string[],
  ) => void;
}

export function useTileSelection(): TileSelectionApi {
  const selectedIds = useSelectionStore(
    (s: SelectionStoreState) => s.selectedFindingIds,
  );
  const activeId = useSelectionStore(
    (s: SelectionStoreState) => s.activeFindingId,
  );
  const selectOne = useSelectionStore((s: SelectionStoreState) => s.selectOne);
  const toggleSelection = useSelectionStore(
    (s: SelectionStoreState) => s.toggleSelection,
  );
  const selectRange = useSelectionStore(
    (s: SelectionStoreState) => s.selectRange,
  );

  const handleClick = useCallback(
    (
      findingId: string,
      event: React.MouseEvent<HTMLElement>,
      visibleIds: string[],
    ): void => {
      if (event.shiftKey) {
        selectRange(findingId, visibleIds);
        return;
      }
      if (event.ctrlKey || event.metaKey) {
        toggleSelection(findingId);
        return;
      }
      selectOne(findingId);
    },
    [selectOne, toggleSelection, selectRange],
  );

  return {
    selectedIds,
    activeId,
    handleClick,
  };
}
